import React, { useState, useRef, useCallback, useEffect } from 'react'
import type { Variable } from '../types'

interface VariableTextareaProps {
  value: string
  onChange: (value: string) => void
  variables: Variable[]
  placeholder?: string
}

export default function VariableTextarea({ value, onChange, variables, placeholder }: VariableTextareaProps) {
  const [showDropdown, setShowDropdown] = useState(false)
  const [filter, setFilter] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const tokenStartRef = useRef<number>(0)

  const filtered = variables.filter((v) =>
    v.name.toLowerCase().includes(filter.toLowerCase())
  )

  const checkForTrigger = useCallback(
    (text: string, cursorPos: number) => {
      const textBefore = text.slice(0, cursorPos)
      const atMatch = textBefore.match(/@(\w*)$/)

      if (atMatch) {
        tokenStartRef.current = cursorPos - atMatch[0].length
        setFilter(atMatch[1])
        setSelectedIndex(0)
        setShowDropdown(true)
      } else {
        setShowDropdown(false)
      }
    },
    []
  )

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value
      onChange(text)
      checkForTrigger(text, e.target.selectionStart)
    },
    [onChange, checkForTrigger]
  )

  const handleClick = useCallback(() => {
    const textarea = textareaRef.current
    if (textarea) {
      checkForTrigger(textarea.value, textarea.selectionStart)
    }
  }, [checkForTrigger])

  const insertVariable = useCallback(
    (varName: string) => {
      const textarea = textareaRef.current
      if (!textarea) return

      const before = value.slice(0, tokenStartRef.current)
      const after = value.slice(textarea.selectionStart)
      const newValue = `${before}@${varName} ${after}`
      onChange(newValue)
      setShowDropdown(false)

      requestAnimationFrame(() => {
        const newPos = tokenStartRef.current + varName.length + 2
        textarea.selectionStart = newPos
        textarea.selectionEnd = newPos
        textarea.focus()
      })
    },
    [value, onChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showDropdown || filtered.length === 0) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, filtered.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (filtered[selectedIndex]) {
          insertVariable(filtered[selectedIndex].name)
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setShowDropdown(false)
      }
    },
    [showDropdown, filtered, selectedIndex, insertVariable]
  )

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return
    const handleOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current && !textareaRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [showDropdown])

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm min-h-24 resize-y focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none transition-all"
        placeholder={placeholder}
      />
      {/* Dropdown — anchored below textarea */}
      {showDropdown && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl py-1 max-h-[200px] overflow-y-auto"
        >
          {filtered.map((v, i) => (
            <button
              key={v.name}
              onMouseDown={(e) => {
                e.preventDefault()
                insertVariable(v.name)
              }}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors ${
                i === selectedIndex ? 'bg-[#5048e5]/10 text-[#5048e5]' : 'hover:bg-slate-50'
              }`}
            >
              <span className="font-mono text-[#5048e5] font-bold text-xs">@</span>
              <span className="font-mono">{v.name}</span>
              <span className="text-xs text-slate-400 ml-auto">{v.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
